import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { DevTool } from '@hookform/devtools';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { useGetTeacherDirectoryQuery, useUpdateTeacherDirectoryMutation } from '@onesource/services';
import { EImageVariant, TUpdateTeacherDirectory, UpdateTeacherDirectorySchema } from '@onesource/schemas';
import {
  buildProcessHandler,
  FILE_FORMATS,
  FormProvider,
  RHFChipInput,
  RHFLanguagesTaughtInput,
  RHFProfessionInput,
  RHFSubmitBtn,
  RHFTextField,
  RhfMediaInlineUpload,
} from '@onesource/components';
import { SHOW_DEV_TOOLS } from 'src/config';

// ----------------------------------------------------------------------

const casualImageProcessHandler = buildProcessHandler([EImageVariant.w400, EImageVariant.original]);

const TeacherFormSchema = UpdateTeacherDirectorySchema.superRefine((value, ctx) => {
  if (!value.profession || value.profession.length === 0) {
    ctx.addIssue({
      path: ['profession'],
      code: z.ZodIssueCode.custom,
      message: 'Pick at least one profession',
    });
  }
  if (!value.casualImage) {
    ctx.addIssue({
      path: ['casualImage'],
      code: z.ZodIssueCode.custom,
      message: 'Casual image is required',
    });
  }
});

type TTeacherFormValues = z.infer<typeof TeacherFormSchema>;

const isFieldFilled = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return value !== null && value !== undefined;
};

const isTeacherProfileComplete = (values: Partial<TTeacherFormValues>): boolean => {
  return (
    isFieldFilled(values.bio) &&
    isFieldFilled(values.profession) &&
    isFieldFilled(values.companyName) &&
    isFieldFilled(values.companyEmail) &&
    isFieldFilled(values.professionalWebsite) &&
    isFieldFilled(values.offerings) &&
    isFieldFilled(values.teachingFormat) &&
    isFieldFilled(values.expertise) &&
    isFieldFilled(values.languagesTaught) &&
    isFieldFilled(values.casualImage)
  );
};

const DEFAULT_LANGUAGES_TAUGHT = ['English'];

export type TTeachersDirectoryTabProps = {
  isTeacher: boolean;
};

export const TeachersDirectoryTab: FC<TTeachersDirectoryTabProps> = ({ isTeacher }) => {
  const { data: teacherDirectory, isLoading } = useGetTeacherDirectoryQuery();
  const { mutateAsync, isPending } = useUpdateTeacherDirectoryMutation();

  const methods = useForm<TTeacherFormValues>({
    resolver: zodResolver(TeacherFormSchema),
    defaultValues: {
      bio: '',
      profession: [],
      companyName: '',
      companyEmail: '',
      professionalWebsite: '',
      offerings: [],
      teachingFormat: undefined,
      expertise: [],
      languagesTaught: DEFAULT_LANGUAGES_TAUGHT,
      casualImage: '',
    },
    mode: 'all',
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = methods;

  const watchedValues = useWatch({ control });
  const isComplete = isTeacherProfileComplete(watchedValues as Partial<TTeacherFormValues>);
  const showWarning = !isTeacher || !isComplete;

  useEffect(() => {
    if (!teacherDirectory) {
      return;
    }
    reset({
      bio: teacherDirectory.bio ?? '',
      profession: teacherDirectory.profession ?? [],
      companyName: teacherDirectory.companyName ?? '',
      companyEmail: teacherDirectory.companyEmail ?? '',
      professionalWebsite: teacherDirectory.professionalWebsite ?? '',
      offerings: teacherDirectory.offerings ?? [],
      teachingFormat: teacherDirectory.teachingFormat ?? undefined,
      expertise: teacherDirectory.expertise ?? [],
      languagesTaught: teacherDirectory.languagesTaught?.length
        ? teacherDirectory.languagesTaught
        : DEFAULT_LANGUAGES_TAUGHT,
      casualImage: teacherDirectory.casualImage ?? '',
    });
  }, [teacherDirectory, reset]);

  const onSubmit = handleSubmit(async data => {
    const payload: TUpdateTeacherDirectory = {
      bio: data.bio,
      profession: data.profession,
      companyName: data.companyName,
      companyEmail: data.companyEmail,
      professionalWebsite: data.professionalWebsite,
      offerings: data.offerings,
      teachingFormat: data.teachingFormat,
      expertise: data.expertise,
      languagesTaught: data.languagesTaught,
      casualImage: data.casualImage,
    };
    await mutateAsync(payload);
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 0 }}>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        {showWarning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Complete your profile and purchase the Teacher Membership to be listed publicly.
          </Alert>
        )}
        <LinearProgress sx={{ mb: 2, borderRadius: 1, visibility: isPending ? 'visible' : 'hidden' }} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 3,
          }}
        >
          {/* Casual Image */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Profile Image (Casual)
            </Typography>
            <Controller
              name="casualImage"
              control={control}
              render={({ field, fieldState }) => (
                <RhfMediaInlineUpload
                  value={field.value ?? undefined}
                  onChange={field.onChange}
                  label="Casual image"
                  processHandler={casualImageProcessHandler}
                  allowedFormats={FILE_FORMATS.IMAGE}
                  selectVariant={EImageVariant.w400}
                  uploadVariants={[EImageVariant.w400]}
                  cropAspectRatio={1}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Box>

          {/* Bio */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Bio
            </Typography>
            <RHFTextField
              name="bio"
              placeholder="Tell members about your teaching practice"
              multiline
              minRows={3}
              disabled={isPending}
            />
          </Box>

          {/* Profession (full row) */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Profession (Occupation)
            </Typography>
            <RHFProfessionInput name="profession" disabled={isPending} />
          </Box>

          {/* Languages taught (full row) */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Languages Taught
            </Typography>
            <RHFLanguagesTaughtInput name="languagesTaught" disabled={isPending} />
          </Box>

          {/* Company name / email */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Company Name
            </Typography>
            <RHFTextField name="companyName" placeholder="Your company" disabled={isPending} />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Company Email
            </Typography>
            <RHFTextField name="companyEmail" placeholder="contact@company.com" disabled={isPending} />
          </Box>

          {/* Professional website (full row) */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Professional Website
            </Typography>
            <RHFTextField name="professionalWebsite" placeholder="https://yourwebsite.com" disabled={isPending} />
          </Box>

          {/* Offerings & Services (full row) */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Offerings & Services
            </Typography>
            <RHFChipInput name="offerings" placeholder="Type an offering and press Enter" disabled={isPending} />
          </Box>

          {/* Teaching Format */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Teaching Format
            </Typography>
            <Controller
              name="teachingFormat"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl error={!!fieldState.error}>
                  <RadioGroup row value={field.value ?? ''} onChange={e => field.onChange(e.target.value || undefined)}>
                    <FormControlLabel value="in_person" control={<Radio />} label="In person" disabled={isPending} />
                    <FormControlLabel value="online" control={<Radio />} label="Online" disabled={isPending} />
                    <FormControlLabel value="both" control={<Radio />} label="Both" disabled={isPending} />
                  </RadioGroup>
                  {fieldState.error && (
                    <Typography variant="caption" color="error">
                      {fieldState.error.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Box>

          {/* Expertise (full row) */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Expertise
            </Typography>
            <RHFChipInput
              name="expertise"
              placeholder="Type an area of expertise and press Enter"
              disabled={isPending}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            pt: 1,
          }}
        >
          <RHFSubmitBtn buttonProps={{ disabled: isPending || !isDirty }}>
            {isPending ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Saving...
              </Box>
            ) : (
              'Save Changes'
            )}
          </RHFSubmitBtn>
        </Box>

        {SHOW_DEV_TOOLS && <DevTool id="account-teachers-directory" control={methods.control} />}
      </FormProvider>
    </Box>
  );
};
